import React from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { COLORS } from '../constants/theme';

interface DashboardCustomizeBarProps {
  readonly isEditing: boolean;
  readonly isSaving: boolean;
  readonly isDirty: boolean;
  readonly canReset: boolean;
  readonly saveError: string | null;
  readonly onStartEditing: () => void;
  readonly onCancel: () => void;
  readonly onReset: () => void;
  readonly onSave: () => void;
}

export const DashboardCustomizeBar: React.FC<DashboardCustomizeBarProps> = ({
  isEditing,
  isSaving,
  isDirty,
  canReset,
  saveError,
  onStartEditing,
  onCancel,
  onReset,
  onSave,
}) => {
  if (!isEditing) {
    return (
      <View style={styles.idleRow}>
        <TouchableOpacity
          style={styles.customizeButton}
          onPress={onStartEditing}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Customize dashboard"
        >
          <Text style={styles.customizeButtonText}>Customize dashboard</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const saveDisabled = isSaving || !isDirty;

  return (
    <View style={styles.editingCard}>
      <View style={styles.editingHeader}>
        <View style={styles.previewBadge}>
          <Text style={styles.previewBadgeText}>Preview</Text>
        </View>
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.secondaryButton, (!canReset || isSaving) && styles.buttonDisabled]}
            onPress={onReset}
            disabled={!canReset || isSaving}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Reset to default"
          >
            <Text style={styles.secondaryButtonText}>Reset to default</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.secondaryButton, isSaving && styles.buttonDisabled]}
            onPress={onCancel}
            disabled={isSaving}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Cancel dashboard changes"
          >
            <Text style={styles.secondaryButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveButton, saveDisabled && styles.buttonDisabled]}
            onPress={onSave}
            disabled={saveDisabled}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Save dashboard"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={COLORS.white} />
            ) : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
      {saveError ? (
        <View style={styles.errorBanner} accessibilityRole="alert">
          <Text style={styles.errorText}>{saveError}</Text>
        </View>
      ) : (
        <Text style={styles.hintText}>Changes are not saved until you press Save.</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  idleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 16,
  },
  customizeButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.primary,
    backgroundColor: COLORS.white,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  customizeButtonText: {
    color: COLORS.primary,
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  editingCard: {
    backgroundColor: COLORS.white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#BFD4DC',
    padding: 16,
    marginBottom: 16,
  },
  editingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  previewBadge: {
    backgroundColor: 'rgba(0,80,110,0.10)',
    borderRadius: 16,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  previewBadgeText: {
    color: COLORS.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Montserrat_700Bold',
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  secondaryButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: COLORS.white,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  secondaryButtonText: {
    color: '#374151',
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  saveButton: {
    minWidth: 72,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: COLORS.primary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  saveButtonText: {
    color: COLORS.white,
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  hintText: {
    marginTop: 10,
    fontSize: 13,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_400Regular',
  },
  errorBanner: {
    marginTop: 12,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  errorText: {
    color: COLORS.error,
    fontSize: 13,
    fontFamily: 'Montserrat_600SemiBold',
    fontWeight: '600',
  },
});
